/**
 * MaskInputTransformer
 *
 * Transforms raw <input> value events using a MaskFormatter.
 * Provides:
 *   - transformInput(rawValue)  → { model, rawModel, displayParts }
 *   - transformModel(model)     → { rawModel, displayParts }
 *
 * displayParts mirrors MaskFormatter.getMaskParts():
 *   { typed: string, ghost: string }
 *
 * Usage:
 *   import { MaskInputTransformer } from '@/helpers/Formatting/MaskInputTransformer';
 *   import { createMaskFormatter } from '@/helpers/Formatting/MaskFormatter';
 *
 *   const transformer = new MaskInputTransformer({
 *     formatter: createMaskFormatter({ maxLength: 11 }),
 *   });
 *
 *   const { model, displayParts } = transformer.transformInput('03123abc');
 *   // model        → '03123'
 *   // displayParts → { typed: '03123', ghost: 'XXXXXX' }
 */
export class MaskInputTransformer {
  /**
   * @param {object}       config
   * @param {MaskFormatter} config.formatter — a MaskFormatter (or any object with
   *                                           format(), unformat(), getMaskParts())
   */
  constructor({ formatter }) {
    this.formatter = formatter;
  }

  /**
   * Called when the <input> fires a native 'input' event.
   *
   * @param {string} rawValue — event.target.value
   * @returns {{ model: string, rawModel: string, displayParts: { typed: string, ghost: string } }}
   */
  transformInput(rawValue) {
    const rawModel = this.formatter.format(rawValue || '');
    const model = this.formatter.unformat(rawModel);
    const displayParts = this.formatter.getMaskParts(rawModel);
    return { model, rawModel, displayParts };
  }

  /**
   * Called when an external model value is set programmatically.
   *
   * @param {string} value — the model/raw value to transform
   * @returns {{ rawModel: string, displayParts: { typed: string, ghost: string } }}
   */
  transformModel(value) {
    const rawModel = this.formatter.format(value || '');
    const displayParts = this.formatter.getMaskParts(rawModel);
    return { rawModel, displayParts };
  }
}

/**
 * Factory helper.
 *
 * @param {object} config
 * @returns {MaskInputTransformer}
 */
export const createMaskInputTransformer = config => new MaskInputTransformer(config);
